import React, { useEffect, useState } from "react";
import {
  clientIcon,
  coldIcon,
  depositIcon,
  payoutIcon,
  teamIcon,
} from "../../utils/historyIcons/historyIcons";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { getTopCustomersByCompanyAndCategoryService } from "../../services/customers/services";
import { useSelector } from "react-redux";
import { IsAllow } from "../../utils/allowHelper/AllowHelper";
import { useTranslator } from "../../context/translator";
import { PERMISSION_CODE } from "../../utils/enum/enums";
import Can from "../can/Can";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Home = () => {
  const { user } = useSelector((state) => state.user);
  const [companyAllow, setCompanyAllow] = useState([]);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();
  const [topCustomerData, setTopCustomerData] = useState({
    labels: ["Customers"],
    datasets: [],
  });
  const [textColor, setTextColor] = useState("black");

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setTextColor(mediaQuery.matches ? "white" : "black");

    const handleChange = (e) => {
      setTextColor(e.matches ? "white" : "black");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);
  const colors = [
    "rgba(75, 192, 192, 1)", // Teal
    "rgba(54, 162, 235, 1)", // Blue
    "rgba(255, 206, 86, 1)", // Yellow
    "rgba(153, 102, 255, 1)", // Purple
    "rgba(103, 224, 104, 1)", // Green
    "rgba(201, 203, 207, 1)", // Light Gray
    "rgba(255, 159, 64, 1)", // Orange
    "rgba(255, 99, 132, 1)", // Pinkish Red
    "rgba(90, 90, 231, 1)", // Deep Blue
    "rgba(245, 66, 152, 1)", // Bright Pink
  ];

  const options = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: textColor,
        },
        position: "top",
      },
      title: {
        display: true,
        text: "Top 10 customers",
        color: textColor,
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
        },
        grid: {
          color:
            textColor === "white"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        ticks: {
          color: textColor,
        },
        grid: {
          color:
            textColor === "white"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
        },
      },
    },
  };

  const getCustomers = React.useCallback(async () => {
    if (!company?._id) return;
    const data = await getTopCustomersByCompanyAndCategoryService({
      companyId: company?._id || "",
    });

    const customers = data?.customers || [];

    if (customers?.code !== PERMISSION_CODE.PERMISSION_DENIED) {
      if (customers?.error === false) {
        return;
      }
      setTopCustomerData((state) => {
        return {
          ...state,
          datasets: customers?.map((customer, index) => ({
            label: customer.fullName,
            data: [customer.totalDeposits],
            backgroundColor: colors[index],
            borderColor: colors[index],
            borderWidth: 1,
          })),
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?._id, userProfile?.user]);

  useEffect(() => {
    if (company?._id) {
      getCustomers();
    }
  }, [getCustomers, company]);

  useEffect(() => {
    if (company && company?.allow) {
      setCompanyAllow({ ...company?.allow });
    }
  }, [company]);

  if (userLoading || companyLoading) return null;

  return (
    <div className="container lg:py-3 max-w-screen-lg ">
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3  ">
        <Can
          permissions={[PERMISSION_CODE.ACCESS_CUSTOMERS]}
          fallback={
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              href="#"
              className="relative block cursor-not-allowed max-w-sm p-6 text-black border border-blue-700 bg-gray-100 dark:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 opacity-50 "
            >
              <h5 className="mb-2 text-2xl font-bold tracking-tight dark:text-white">
                {translate("customers")}
              </h5>
              <p className="font-normal dark:text-gray-400">
                {translate("administrate-customers")}
              </p>
              <div className="absolute inset-y-3 right-3 hover:text-white">
                {clientIcon()}
              </div>
            </a>
          }
        >
          <a
            href="/clients"
            className="relative block order-last max-w-sm p-6 text-black border border-blue-700 hover:bg-gray-200 hover:dark:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 "
          >
            <div className="">
              <h5 className="mb-2 text-2xl font-bold tracking-tight dark:text-white">
                {translate("customers")}
              </h5>
              <p className="font-normal dark:text-gray-400 ">
                {translate("administrate-customers")}
              </p>
              <div className="absolute inset-y-3 right-3">{clientIcon()}</div>
            </div>
          </a>
        </Can>

        <Can
          permissions={[PERMISSION_CODE.ACCESS_TEAM]}
          fallback={
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              href="#"
              className="relative block cursor-not-allowed max-w-sm p-6 text-black border border-blue-700 bg-gray-100 dark:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 opacity-50 "
            >
              <h5 className="mb-2 text-2xl font-bold tracking-tight dark:text-white">
                {translate("team")}
              </h5>
              <p className="font-normal dark:text-gray-400">
                {translate("see-manage-teams")}
              </p>
              <div className="absolute inset-y-3 right-3 hover:text-white">
                {teamIcon()}
              </div>
            </a>
          }
        >
          <a
            href="/team"
            className="relative block max-w-sm p-6 text-black border border-blue-700 hover:bg-gray-200 hover:dark:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 hover:stroke-white "
          >
            <h5 className="mb-2 text-2xl font-bold tracking-tight dark:text-white">
              {translate("team")}
            </h5>
            <p className="font-normal dark:text-gray-400">
              {translate("see-manage-teams")}
            </p>
            <div className="absolute inset-y-3 right-3 hover:text-white">
              {teamIcon()}
            </div>
          </a>
        </Can>
        <>
          {IsAllow(companyAllow, "SBFields") && (
            <>
              <Can
                permissions={[PERMISSION_CODE.VIEW_SPECIAL_CUSTOMERS]}
                fallback={
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <a
                    href="#"
                    className="relative block cursor-not-allowed max-w-sm p-6 text-black border border-blue-700 bg-gray-100 dark:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 opacity-50 "
                  >
                    <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {translate("cold-accounts")}
                    </h5>
                    <p className="font-normal text-gray-700 dark:text-gray-400">
                      {translate("filter-inactive-acounts")}
                    </p>
                    <div className="absolute inset-y-3 right-3">
                      {coldIcon()}
                    </div>
                  </a>
                }
              >
                <a
                  href="/clients/cold"
                  className="relative block max-w-sm p-6 text-black border border-blue-700 hover:bg-gray-200 hover:dark:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 "
                >
                  <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                    {translate("cold-accounts")}
                  </h5>
                  <p className="font-normal text-gray-700 dark:text-gray-400">
                    {translate("filter-inactive-acounts")}
                  </p>
                  <div className="absolute inset-y-3 right-3">{coldIcon()}</div>
                </a>
              </Can>
              <a
                href="/clients/deposits"
                className="hidden relative max-w-sm p-6 text-black border border-blue-700 hover:bg-gray-200 hover:dark:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 "
              >
                <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {translate("deposit")}
                </h5>
                <p className="font-normal text-gray-700 dark:text-gray-400">
                  description
                </p>
                <div className="absolute inset-y-3 right-3">
                  {depositIcon()}
                </div>
              </a>
              <a
                href="/clients/payments"
                className="relative hidden max-w-sm p-6 text-black border border-blue-700 hover:bg-gray-200 hover:dark:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm dark:border-blue-500 dark:text-blue-500 "
              >
                <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {translate("payouts")}
                </h5>
                <p className="font-normal text-gray-700 dark:text-gray-400">
                  description
                </p>
                <div className="absolute inset-y-3 right-3">{payoutIcon()}</div>
              </a>
            </>
          )}
        </>
      </div>
      <Can permissions={[PERMISSION_CODE.ACCESS_CUSTOMERS]}>
        <div className="grid w-full gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
          <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-4">
            <Bar data={topCustomerData} options={options} />
          </div>
        </div>
      </Can>
    </div>
  );
};

export default Home;
